import { motion, useScroll, useTransform, useMotionValue, useInView, animate } from "framer-motion";
import { useRef, useEffect } from "react";
import { Check, Plus } from "lucide-react";

interface StepVisualProps {
  autoplay?: boolean;
}

const StylistAnalysisStep = ({ autoplay = false }: StepVisualProps = {}) => {
  const targetRef = useRef(null);
  const { scrollYProgress: scrollProg } = useScroll({
    target: targetRef,
    offset: ["start end", "end start"]
  });
  const autoMV = useMotionValue(0);
  const inView = useInView(targetRef, { amount: 0.4 });
  useEffect(() => {
    if (!autoplay) return;
    if (inView) {
      const controls = animate(autoMV, 1, { duration: 1.8, ease: "easeInOut", delay: 0.1 });
      return () => controls.stop();
    }
    autoMV.set(0);
  }, [autoplay, inView, autoMV]);
  const scrollYProgress = autoplay ? autoMV : scrollProg;

  // Calculate perfect 2x2 grid geometry (similar to step 1)
  // Card base dimensions: 128x160. Final scale is 0.75 (96x120px)
  // Desired uniform visual gap: 20px
  // Delta X = 96 + 20 = 116px (col1 = -58, col2 = 58)
  // Delta Y = 120 + 20 = 140px (row1 = -70, row2 = 70)
  
  const col1 = -58;
  const col2 = 58;
  const row1 = -70;
  const row2 = 70;

  const items = [
    { id: 1, img: "/stylist-2.png", chaos: { x: -120, y: -60, r: -15 }, grid: { x: col1, y: row1 } },
    { id: 2, img: "/stylist-1.png", chaos: { x: 100, y: -80, r: 20 }, grid: { x: col2, y: row1 } },
    { id: 3, img: "/stylist-3.png", chaos: { x: -100, y: 120, r: -25 }, grid: { x: col1, y: row2 } },
  ];

  return (
    <div ref={targetRef} className="relative w-full h-[400px] sm:h-[500px] bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-white" />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:24px_24px] opacity-40" />

      <div className="relative h-full w-full flex items-center justify-center perspective-1000">
        
        {/* The 3 Clothing Items */}
        {items.map((item, index) => (
          <motion.div 
            key={item.id} 
            className="absolute w-24 h-32 sm:w-32 sm:h-40 bg-white shadow-md rounded-sm overflow-hidden border border-slate-200" 
            style={{
              top: "50%",
              left: "50%",
              x: useTransform(scrollYProgress, [0.0, 0.25, 0.45], [0, item.chaos.x, item.grid.x]),
              y: useTransform(scrollYProgress, [0.0, 0.25, 0.45], [0, item.chaos.y, item.grid.y]),
              rotate: useTransform(scrollYProgress, [0.0, 0.25, 0.45], [0, item.chaos.r, 0]),
              scale: useTransform(scrollYProgress, [0.0, 0.25, 0.45], [0.5, 0.8, 0.75]),
              opacity: useTransform(scrollYProgress, [0, 0.1], [0, 1]),
              zIndex: 10,
              marginTop: -64,
              marginLeft: -48
            }}
          >
            <div className="w-full h-full bg-white rounded-sm overflow-hidden relative border border-slate-100 group">
              <img src={item.img} alt="Wardrobe Item" className="w-full h-full object-cover mix-blend-multiply" />
            </div>
          </motion.div>
        ))}

        {/* HUD Targeting Line overlaying the grid */}
        <motion.div 
          className="absolute left-[20%] right-[20%] h-[2px] bg-[#c2410c] shadow-[0_0_15px_3px_rgba(234,88,12,0.5)] z-20"
          style={{
            top: useTransform(scrollYProgress, [0.2, 0.4], ["30%", "65%"]),
            opacity: useTransform(scrollYProgress, [0.2, 0.25, 0.35, 0.4], [0, 1, 1, 0])
          }}
        />



        {/* The Gap Sketch (Recommended Addition) drops into col2, row2 */}
        <motion.div 
          className="absolute bg-white/95 backdrop-blur-md p-1 shadow-[0_10px_30px_rgba(234,88,12,0.12)] rounded-sm border-2 border-[#c2410c] transform origin-center z-10 overflow-hidden" 
          style={{
              top: "50%",
              left: "50%",
              width: 128,  // matches sm:w-32 exactly
              height: 160, // matches sm:h-40 exactly
              x: col2,
              y: useTransform(scrollYProgress, [0.35, 0.45], [100, row2]), // Slides up perfectly into place
              rotate: 0,
              scale: 0.75, // Matches the global 0.75 scale exactly
              opacity: useTransform(scrollYProgress, [0.35, 0.45], [0, 1]),
              marginTop: -64,
              marginLeft: -48
          }}
        >
          <div className="w-full h-full bg-[#c2410c]/5 flex flex-col items-center justify-center gap-4 border border-[#c2410c]/20 rounded-[2px]">
            <div className="w-10 h-10 rounded-sm bg-[#c2410c]/10 flex items-center justify-center text-[#c2410c] border border-[#c2410c]/20 shadow-inner">
              <Plus className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col items-center gap-1">
              <span className="text-[9px] text-[#c2410c] font-bold tracking-[0.2em] uppercase text-center leading-tight">Recommended<br/>Blazer</span>
            </div>
          </div>
        </motion.div>

        {/* HUD Note 2 (Points to the Gap) */}
        <motion.div 
          className="absolute z-30 font-bold bg-[#c2410c]/10 text-[#c2410c] px-2 py-1 text-[9px] uppercase tracking-[0.2em] border border-[#c2410c]/30 rounded-sm backdrop-blur-md shadow-sm" 
          style={{
            top: "70%",
            left: "58%",
            opacity: useTransform(scrollYProgress, [0.45, 0.55], [0, 1]),
            x: useTransform(scrollYProgress, [0.45, 0.55], [-10, 0]),
          }}
        >
          Critical Gap
        </motion.div>

        {/* Final Success Badge (HUD Style) */}
        <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 px-5 py-3 bg-[#0f172a]/95 backdrop-blur-md border border-slate-700 rounded-md shadow-[0_20px_40px_rgba(0,0,0,0.4)] z-40 whitespace-nowrap" style={{
          opacity: useTransform(scrollYProgress, [0.55, 0.65], [0, 1]),
          y: useTransform(scrollYProgress, [0.55, 0.65], [20, 0]),
          scale: useTransform(scrollYProgress, [0.55, 0.65], [0.9, 1])
        }}>
          <div className="w-6 h-6 rounded-sm bg-[#c2410c] flex items-center justify-center text-white shadow-md">
            <Check className="w-3.5 h-3.5" strokeWidth={3} />
          </div>
          <div className="flex flex-col">
            <span className="text-[8px] font-bold text-[#c2410c] tracking-[0.2em] uppercase leading-none mb-1">Audit Complete</span>
            <span className="text-[12px] font-bold text-white leading-none tracking-widest uppercase">Gaps Identified</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default StylistAnalysisStep;